import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, NavLink } from "react-router-dom";
import { Menu, X, ChevronDown, ArrowUpRight } from "lucide-react";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useT } from "@/lib/i18n";

export default function Header({ variant = "light" }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [dropdown, setDropdown] = useState(false);
  const loc = useLocation();
  const dropRef = useRef(null);
  const timer = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); setDropdown(false); }, [loc.pathname]);

  const dark = variant === "dark";
  const linkColor = scrolled ? "text-ink" : (dark ? "text-white" : "text-ink");
  const expertises = t("nav.expertiseList") || [];
  const COLORS = ["#E62C86", "#F3A114", "#EEE316", "#9DBC1D", "#52A385", "#1C1580", "#E62C86"];

  const PRIMARY = [
    { label: t("nav.about"), path: "/a-propos" },
    { label: t("nav.team"), path: "/equipe" },
    { label: t("nav.references"), path: "/references" },
    { label: t("nav.blog"), path: "/blog" },
  ];

  const onEnter = () => { clearTimeout(timer.current); setDropdown(true); };
  const onLeave = () => { timer.current = setTimeout(() => setDropdown(false), 180); };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-40 transition-all duration-500 ${
        scrolled ? "backdrop-blur-xl bg-paper/90 border-b border-ink/10" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-14 h-24 flex items-center justify-between gap-8">
        <Link to="/" data-testid="header-logo" className="flex items-center shrink-0">
          <img
            src="/brand/wedo_logo_2024.png"
            alt="WeDo Coaching"
            className={`h-[72px] md:h-[90px] transition-opacity ${scrolled ? "" : (dark ? "invert brightness-0 contrast-200" : "")}`}
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          <div className="relative" onMouseEnter={onEnter} onMouseLeave={onLeave} ref={dropRef}>
            <button
              data-testid="nav-expertises"
              className={`flex items-center gap-1.5 text-[15px] tracking-wide font-semibold ${linkColor} opacity-90 hover:opacity-100`}
            >
              {t("nav.expertises")}
              <ChevronDown size={16} className={`transition-transform ${dropdown ? "rotate-180" : ""}`} />
            </button>
            {dropdown && (
              <div data-testid="expertises-dropdown" className="absolute left-1/2 -translate-x-1/2 top-full pt-5">
                <div className="bg-paper shadow-2xl border border-ink/10 w-[760px] grid grid-cols-2 overflow-hidden">
                  {expertises.map((e, i) => (
                    <Link
                      key={e.path}
                      to={e.path}
                      data-testid={`dropdown-${e.path.replace(/\//g, '')}`}
                      className="group relative p-6 border-b border-r border-ink/8 last:border-r-0 hover:bg-paper-2 transition-colors"
                    >
                      <div className="absolute left-0 top-0 bottom-0 w-1 transition-all" style={{ background: COLORS[i % COLORS.length] }} />
                      <div className="flex items-start justify-between gap-4 pl-2">
                        <div>
                          <p className="display text-[17px] leading-tight text-ink">{e.label}</p>
                          <p className="text-[13px] leading-snug text-ink/75 mt-1.5 font-normal">{e.desc}</p>
                        </div>
                        <ArrowUpRight size={18} className="text-ink/30 group-hover:text-brand transition-colors shrink-0 mt-0.5" />
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          {PRIMARY.map(n => (
            <NavLink
              key={n.path}
              to={n.path}
              data-testid={`nav-${n.path.replace(/\//g, '')}`}
              className={({ isActive }) =>
                `editorial-link text-[15px] tracking-wide font-semibold ${linkColor} ${isActive ? "opacity-100" : "opacity-90 hover:opacity-100"}`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-5 shrink-0">
          <a
            href="https://diagnostic.wedocoaching.com"
            target="_blank"
            rel="noreferrer"
            data-testid="header-diagnostic"
            className={`editorial-link text-[15px] tracking-wide font-semibold ${linkColor} opacity-90 hover:opacity-100`}
          >
            {t("nav.diagnostic")}
          </a>
          <LanguageSwitcher tone={scrolled ? "light" : (dark ? "dark" : "light")} />
          <Link
            to="/contact"
            data-testid="header-cta"
            className={`btn-pill ${scrolled ? "btn-primary" : (dark ? "btn-light" : "btn-primary")}`}
          >
            {t("common.cta.contactShort")}
          </Link>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen(o => !o)}
          className={`lg:hidden p-2 ${linkColor}`}
          aria-label="Menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-paper border-t border-ink/10 max-h-[80vh] overflow-y-auto" data-testid="mobile-menu">
          <div className="px-6 py-6 flex flex-col gap-1">
            <p className="text-[11px] uppercase tracking-[0.25em] text-ink/60 mt-2 mb-2">{t("nav.expertises")}</p>
            {expertises.map((e, i) => (
              <Link
                key={e.path}
                to={e.path}
                className="text-ink text-[15px] font-medium py-2.5 border-l-2 pl-3 transition-colors hover:text-brand"
                style={{ borderColor: COLORS[i % COLORS.length] }}
                data-testid={`mobile-nav-${e.path.replace(/\//g, '')}`}
              >
                {e.label}
              </Link>
            ))}
            <p className="text-[11px] uppercase tracking-[0.25em] text-ink/60 mt-6 mb-2">Navigation</p>
            {PRIMARY.map((n) => (
              <Link key={n.path} to={n.path} className="text-ink text-[15px] font-medium py-2"
                data-testid={`mobile-nav-${n.path.replace(/\//g, '')}`}>
                {n.label}
              </Link>
            ))}
            <a
              href="https://diagnostic.wedocoaching.com"
              target="_blank"
              rel="noreferrer"
              className="text-brand text-[15px] font-medium py-2 inline-flex items-center gap-2"
              data-testid="mobile-nav-diagnostic"
            >
              {t("nav.diagnostic")} <ArrowUpRight size={14} />
            </a>
            <Link to="/contact" className="btn-pill btn-primary mt-4 justify-center" data-testid="mobile-nav-contact">
              {t("common.cta.contactShort")}
            </Link>
            <div className="mt-4 flex justify-center">
              <LanguageSwitcher tone="light" />
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
